// ─────────────────────────────────────────────────────────────
// useFirestore.js — Hooks de synchronisation Firebase
// Toutes les données sont lues/écrites dans Firestore
// ─────────────────────────────────────────────────────────────
import { useEffect, useCallback } from 'react';
import {
  doc, setDoc, getDoc, onSnapshot,
  collection, addDoc, updateDoc, deleteDoc,
  query, orderBy, limit, getDocs, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

// ── Noms des collections Firestore ───────────────────────────
const COL = {
  config:       'config',        // adminPwd, domaines, formations, tarifs, produits, pipeline...
  transactions: 'transactions',  // factures, devis, commandes boutique, paiements sur devis
  workers:      'workers',       // personnel
  etudiants:    'etudiants',     // étudiants & alumni
  photos:       'photos',        // galeries photos
  depenses:     'depenses',      // dépenses (personnel, autres) — Admin → Finances
  visites:      'visites',       // analytics anonymisées des visites du site
};

// ════════════════════════════════════════════════════════════
// HOOK : Charger + écouter la config globale (temps réel)
// ════════════════════════════════════════════════════════════
export function useConfigSync(setData, setAdminPwd, initialData, ADMIN_PWD_DEFAULT) {
  useEffect(() => {
    const ref = doc(db, COL.config, 'main');

    // Écouter en temps réel — se met à jour automatiquement
    const unsub = onSnapshot(ref, async (snap) => {
      if (snap.exists()) {
        const remote = snap.data();
        // Fusionner avec initialData pour les clés manquantes
        setData(prev => ({
          ...initialData,
          ...prev,
          domaines:   remote.domaines   ?? prev.domaines,
          formations: remote.formations ?? prev.formations,
          immobilier: remote.immobilier ?? prev.immobilier,
          tarifs:     remote.tarifs     ?? prev.tarifs,
          contact:    remote.contact    ?? prev.contact,
          paymentInfo: remote.paymentInfo ?? prev.paymentInfo,
          photosEntreprise: remote.photosEntreprise ?? prev.photosEntreprise,
          photosDomaines: remote.photosDomaines ?? prev.photosDomaines,
          workers:    remote.workers    ?? prev.workers,
          produits:   remote.produits   ?? prev.produits,
          pipeline:   remote.pipeline   ?? prev.pipeline,
          boutiqueConfig: remote.boutiqueConfig ?? prev.boutiqueConfig,
        }));
        if (remote.adminPwd) setAdminPwd(remote.adminPwd);
      } else {
        // Première utilisation : initialiser Firestore avec les données par défaut
        await setDoc(ref, {
          domaines:    initialData.domaines,
          formations:  initialData.formations,
          immobilier:  initialData.immobilier  || [],
          tarifs:      initialData.tarifs       || [],
          contact:     initialData.contact      || {},
          paymentInfo: initialData.paymentInfo  || {},
          photosEntreprise: initialData.photosEntreprise || [],
          photosDomaines:   initialData.photosDomaines   || [],
          workers:     initialData.workers,
          produits:    initialData.produits     || [],
          pipeline:    initialData.pipeline     || [],
          boutiqueConfig: initialData.boutiqueConfig || { delaiGraceJours: 3 },
          adminPwd:    ADMIN_PWD_DEFAULT,
        });
      }
    }, (err) => {
      console.warn('Firebase config sync error:', err.message);
    });

    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ════════════════════════════════════════════════════════════
// HOOK : Charger + écouter les transactions (temps réel)
// ════════════════════════════════════════════════════════════
export function useTransactionsSync(setData) {
  useEffect(() => {
    const q = query(
      collection(db, COL.transactions),
      orderBy('id', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      const transactions = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
      setData(prev => ({ ...prev, transactions }));
    }, (err) => {
      console.warn('Firebase transactions sync error:', err.message);
    });

    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ════════════════════════════════════════════════════════════
// HOOK : Charger + écouter les étudiants (temps réel)
// ════════════════════════════════════════════════════════════
export function useEtudiantsSync(setData) {
  useEffect(() => {
    const q = query(collection(db, COL.etudiants), orderBy('id', 'asc'));

    const unsub = onSnapshot(q, (snap) => {
      const etudiants = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
      setData(prev => ({ ...prev, etudiants }));
    }, (err) => {
      console.warn('Firebase etudiants sync error:', err.message);
    });

    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ════════════════════════════════════════════════════════════
// HOOK : Charger + écouter les dépenses (temps réel) — Admin → Finances
// ════════════════════════════════════════════════════════════
export function useDepensesSync(setData) {
  useEffect(() => {
    const q = query(collection(db, COL.depenses), orderBy('date', 'desc'));

    const unsub = onSnapshot(q, (snap) => {
      const depenses = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
      setData(prev => ({ ...prev, depenses }));
    }, (err) => {
      console.warn('Firebase depenses sync error:', err.message);
    });

    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ════════════════════════════════════════════════════════════
// HOOK : Charger + écouter les visites récentes (temps réel)
// Limité aux 500 dernières pour ne pas surcharger — suffisant pour
// des statistiques agrégées (jour/mois/année), pas un journal complet.
// ════════════════════════════════════════════════════════════
export function useVisitesSync(setData) {
  useEffect(() => {
    const q = query(collection(db, COL.visites), orderBy('ts', 'desc'), limit(500));

    const unsub = onSnapshot(q, (snap) => {
      const visites = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
      setData(prev => ({ ...prev, visites }));
    }, (err) => {
      console.warn('Firebase visites sync error:', err.message);
    });

    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ════════════════════════════════════════════════════════════
// FONCTIONS D'ÉCRITURE
// ════════════════════════════════════════════════════════════

// ── Sauvegarder la config principale ────────────────────────
export async function saveConfig(patch) {
  const ref = doc(db, COL.config, 'main');
  await setDoc(ref, patch, { merge: true });
}

// ── Sauvegarder le mot de passe admin ───────────────────────
export async function saveAdminPwd(pwd) {
  await saveConfig({ adminPwd: pwd });
}

// ── Ajouter une transaction (facture/devis) ──────────────────
export async function addTransaction(transactionData) {
  await addDoc(collection(db, COL.transactions), transactionData);
}

// ── Mettre à jour une transaction ───────────────────────────
export async function patchTransaction(docId, patch) {
  if (!docId) return;
  const ref = doc(db, COL.transactions, docId);
  await updateDoc(ref, patch);
}

// ── Supprimer une transaction ────────────────────────────────
export async function removeTransaction(docId) {
  if (!docId) return;
  await deleteDoc(doc(db, COL.transactions, docId));
}

// ── Ajouter un étudiant ──────────────────────────────────────
export async function addEtudiant(etudiantData) {
  await addDoc(collection(db, COL.etudiants), etudiantData);
}

// ── Mettre à jour un étudiant ────────────────────────────────
export async function patchEtudiant(docId, patch) {
  if (!docId) return;
  await updateDoc(doc(db, COL.etudiants, docId), patch);
}

// ── Supprimer un étudiant ────────────────────────────────────
export async function removeEtudiant(docId) {
  if (!docId) return;
  await deleteDoc(doc(db, COL.etudiants, docId));
}

// ── Ajouter une dépense (Admin → Finances) ──────────────────
export async function addDepense(depenseData) {
  await addDoc(collection(db, COL.depenses), depenseData);
}

// ── Supprimer une dépense ────────────────────────────────────
export async function removeDepense(docId) {
  if (!docId) return;
  await deleteDoc(doc(db, COL.depenses, docId));
}

// ── Enregistrer une visite (anonyme/agrégée uniquement) ──────
// Appelé une fois par chargement du site — jamais d'identité ni
// d'adresse postale réelle, uniquement des données de géolocalisation
// approximative (ville/pays déduits de l'IP) et d'appareil.
export async function logVisite(visiteData) {
  try {
    await addDoc(collection(db, COL.visites), visiteData);
  } catch (e) {
    console.warn('logVisite error:', e.message);
  }
}

// ── Export complet vers JSON (sauvegarde manuelle) ───────────
export async function exportAllData() {
  const result = {};

  // Config
  const configSnap = await getDoc(doc(db, COL.config, 'main'));
  result.config = configSnap.exists() ? configSnap.data() : {};

  // Transactions
  const txSnap = await getDocs(collection(db, COL.transactions));
  result.transactions = txSnap.docs.map(d => d.data());

  // Étudiants
  const etSnap = await getDocs(collection(db, COL.etudiants));
  result.etudiants = etSnap.docs.map(d => d.data());

  return result;
}

// ── Import depuis JSON (restauration) ───────────────────────
export async function importAllData(jsonData) {
  const batch = writeBatch(db);

  // Config
  if (jsonData.config) {
    batch.set(doc(db, COL.config, 'main'), jsonData.config, { merge: true });
  }

  // Transactions — effacer et réimporter
  if (jsonData.transactions?.length) {
    for (const tx of jsonData.transactions) {
      const ref = doc(collection(db, COL.transactions));
      batch.set(ref, tx);
    }
  }

  // Étudiants — effacer et réimporter
  if (jsonData.etudiants?.length) {
    for (const et of jsonData.etudiants) {
      const ref = doc(collection(db, COL.etudiants));
      batch.set(ref, et);
    }
  }

  await batch.commit();
}
